import React, { useCallback, useEffect, useState } from 'react';
import { fetchJSON } from '../../utils/api';
import { inputCls, inputBaseCls } from '../shared/FormKit';

// L&D Setup → Training Settings (single-row ld_training_settings).
// Two knobs for the Professional Development dashboard: the
// yearly internal-MANDATORY hours target, and when the training year starts.
// The period end is derived — always the day before the next start — so a
// 1 Oct start reads as "1 Oct – 30 Sep" with no way to configure a gap.
//
// A panel rather than a page: the L&D Setup screen already gates on
// `ld:setup:edit`, so this renders inside its tab bar next to the lists.

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const DAYS_IN_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

// 'Oct 1 – Sep 30' preview for a given start (month 1-12, day 1-31).
const periodPreview = (month, day) => {
  const short = (m) => MONTHS[m - 1].slice(0, 3);
  const end = new Date(2001, month - 1, day); // any non-leap year
  end.setDate(end.getDate() - 1);
  return `${short(month)} ${day} – ${short(end.getMonth() + 1)} ${end.getDate()}`;
};

// Side-by-side labels wrap to different heights (the hours target takes two
// lines, the training year one), which would leave the two inputs on
// different baselines. Reserving two lines of label height in the two-column
// layout lines the inputs up; below `sm` the fields stack, so the reservation
// is dropped.
const Field = ({ label, hint, children }) => (
  <div>
    <label className="block text-sm font-medium text-slate-700 mb-1.5 sm:min-h-[2.5rem]">{label}</label>
    {children}
    {hint && <p className="text-xs text-slate-500 mt-1">{hint}</p>}
  </div>
);

const TrainingSettingsPanel = () => {
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const data = await fetchJSON('/api/ld/settings');
      setForm({
        mandatoryHoursTarget: String(data.settings.mandatoryHoursTarget),
        periodStartMonth: data.settings.periodStartMonth,
        periodStartDay: data.settings.periodStartDay,
      });
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    const target = Number(form.mandatoryHoursTarget);
    if (!Number.isFinite(target) || target <= 0) {
      setError('The mandatory hours target must be a number greater than 0.');
      return;
    }
    setSaving(true); setError(null); setSavedMsg(false);
    try {
      const data = await fetchJSON('/api/ld/settings', {
        method: 'PATCH',
        body: JSON.stringify({
          mandatoryHoursTarget: target,
          periodStartMonth: form.periodStartMonth,
          periodStartDay: form.periodStartDay,
        }),
      });
      setForm({
        mandatoryHoursTarget: String(data.settings.mandatoryHoursTarget),
        periodStartMonth: data.settings.periodStartMonth,
        periodStartDay: data.settings.periodStartDay,
      });
      setSavedMsg(true);
      setTimeout(() => setSavedMsg(false), 2500);
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  if (!form && !loading && error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
        <h2 className="text-lg font-semibold text-red-800">Failed to load settings</h2>
        <p className="text-sm text-red-600 mt-1">{error}</p>
        <button
          onClick={load}
          className="mt-4 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700"
        >
          Retry
        </button>
      </div>
    );
  }

  if (loading || !form) {
    return <div className="p-8 text-center text-slate-500">Loading…</div>;
  }

  const maxDay = DAYS_IN_MONTH[form.periodStartMonth - 1];

  return (
    <div className="max-w-3xl">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm mb-4">
          {error}
        </div>
      )}

      <section className="bg-white rounded-lg border border-slate-200">
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b border-slate-200">
          <h2 className="text-base font-semibold text-slate-900">Mandatory Training</h2>
          <div className="flex items-center gap-3">
            {savedMsg && <span className="text-sm text-green-600">Saved</span>}
            <button onClick={save} disabled={saving} className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50">
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
        <div className="p-4 space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field
              label="Mandatory Hours Target (Per Person, per Training Year)"
              hint="Counted from internal MANDATORY sessions only — non-mandatory and External CPD hours are reported separately and never offset this target."
            >
              <input
                type="number" min="1" step="0.5"
                value={form.mandatoryHoursTarget}
                onChange={e => setForm(prev => ({ ...prev, mandatoryHoursTarget: e.target.value }))}
                className={inputCls}
              />
            </Field>
            <Field
              label="Training Year Starts On"
              hint={`The year always ends the day before the next start: ${periodPreview(form.periodStartMonth, Math.min(form.periodStartDay, maxDay))}. Dashboard date filters open on this period.`}
            >
              <div className="flex gap-2">
                <select
                  value={form.periodStartMonth}
                  onChange={e => {
                    const month = Number(e.target.value);
                    setForm(prev => ({
                      ...prev,
                      periodStartMonth: month,
                      periodStartDay: Math.min(prev.periodStartDay, DAYS_IN_MONTH[month - 1]),
                    }));
                  }}
                  className={inputCls}
                >
                  {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
                </select>
                <select
                  value={form.periodStartDay}
                  onChange={e => setForm(prev => ({ ...prev, periodStartDay: Number(e.target.value) }))}
                  className={`${inputBaseCls} w-24`}
                >
                  {Array.from({ length: maxDay }, (_, i) => i + 1).map(d => (
                    <option key={d} value={d}>{d}</option>
                  ))}
                </select>
              </div>
            </Field>
          </div>
          <p className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg p-3">
            Defaults: 40 hours, 1 October – 30 September. Changes apply to the Professional
            Development dashboard immediately (refresh the page to pick them up).
          </p>
        </div>
      </section>
    </div>
  );
};

export default TrainingSettingsPanel;
